/**
 * components/segmentedBar.tsx
 * The 6px segmented score bar from the design — a rounded track with up to N
 * colored segments representing a composite score (e.g. the Stage-3 breakdown:
 * nice-to-have / experience / title / tenure / recency). Segment widths are
 * proportional to each part's value over `max`.
 */
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { web } from '../utils/web';
import { toneColors, type Tone } from '../theme/tone';

export interface BarSegment {
  value: number;
  tone: Tone;
}

export interface SegmentedBarProps {
  segments: BarSegment[];
  /** Total the segments are measured against (default = sum of values). */
  max?: number;
  height?: number;
  /** Grow each segment from 0 → its width on mount (web-only; inert on native). */
  grow?: boolean;
  /** Stagger, in ms, before the grow transition starts. */
  delay?: number;
}

export default function SegmentedBar({ segments, max, height = 6, grow = false, delay = 0 }: SegmentedBarProps) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  const total = max ?? segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
  const denom = total > 0 ? total : 1;

  // Flip to grown on the frame after mount so the 0-width paints first.
  const [grown, setGrown] = useState(!grow);
  useEffect(() => {
    if (!grow) return;
    const raf = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(raf);
  }, [grow]);

  return (
    <View
      style={{
        flexDirection: 'row',
        height,
        borderRadius: radii.pill,
        backgroundColor: colors.paper3,
        overflow: 'hidden',
      }}
    >
      {segments.map((seg, i) => {
        const pct = Math.max(0, (seg.value / denom) * 100);
        if (pct <= 0) return null;
        return (
          <View
            key={i}
            style={[
              { width: `${grown ? pct : 0}%`, height, backgroundColor: toneColors(colors, seg.tone).fg },
              grow && web({ transition: 'width 720ms cubic-bezier(.2,.7,.2,1)', transitionDelay: `${delay + i * 60}ms` }),
            ]}
          />
        );
      })}
    </View>
  );
}
